import { notifications } from '@mantine/notifications';
import { IconCopy } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import DeviceForm, { DeviceFormData } from 'components/forms/device/DeviceForm';
import { DeviceFileType } from 'model/DeviceFileType';
import { stringifyDate } from 'components/inputs/DateInput';
import { addMonths } from 'date-fns';
import { DEVICE_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DEVICES_PAGE_PATH, EDIT_DEVICE_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { randomId } from '@mantine/hooks';

/**
 * Page used to copy a device.
 * Creates a copy of an existing device with all data except faults pre-filled.
 *
 */
export default function CopyDevicePage() {
  const navigate = useNavigate();
  const { getAction } = useApi();
  const { deviceId: originalDeviceId } = useParams();

  const {
    data: originalDevice,
    loading,
    error,
  } = useImmediateAction(() => {
    const action = getAction('DeviceGet');
    return action({ parameters: { deviceId: originalDeviceId! } });
  });

  if (!originalDeviceId) {
    return <Navigate to={DEVICES_PAGE_PATH.original} />;
  }

  if (error) {
    panic(error);
    return <></>;
  }

  /**
   * Retrieves files of a specific type as a flat array with type set.
   */
  function retrieveDeviceFiles(files: NonNullable<typeof originalDevice>['files'], tabName: DeviceFileType) {
    return (files ?? [])
      .filter((file) => file.fileType === tabName)
      .map((file) => ({
        fileName: file.fileName,
        fileId: file.fileId,
        key: randomId(),
        uploadedAt: new Date(file.created!),
        originalFileName: '',
        deleted: false as const,
        isMainImage: file.isMainImage,
        hasKepSignature: file.hasKepSignature,
        type: tabName,
      }));
  }

  /**
   * Runs when the form is submitted.
   */
  function onSubmit({
    deviceName,
    manufacturer,
    serialNumber,
    manufactured,
    internalPossessionsNumber,
    internalNote,
    building,
    floor,
    room,
    longitude,
    latitude,
    deviceTypeId,
    deviceSubtypeId,
    departmentId,
    responsiblePersonId,
    responsiblePersonNote,
    nextRevision,
    files,
    evidenceFields,
    structure = 'simple',
    parentDeviceId,
    childDeviceIds,
  }: DeviceFormData) {
    const deviceCreateAction = getAction('DeviceCreate');

    const mappedFiles = files.map((val) => ({
      fileName: val.fileName,
      fileId: val.fileId,
      fileType: val.type,
      isMainImage: val.isMainImage,
    }));

    const mappedRevisions = nextRevision
      .filter(({ description, revisionPeriod }) => Number(revisionPeriod) !== 0 && description !== '')
      .filter(({ deleted }) => !deleted)
      .map((revision) => {
        let date: string | undefined = '';

        if (revision.neverExecuted) {
          date = revision.nextDate !== '' ? revision.nextDate! : undefined;
        } else {
          date = revision.date !== '' ? revision.nextDate! : undefined;
        }

        return {
          description: revision.description,
          date,
          revisionPeriod: Number(revision.revisionPeriod),
          neverExecuted: revision.neverExecuted,
          deviceTypeId: revision.deviceTypeId ? Number(revision.deviceTypeId) : undefined,
        };
      });

    return deviceCreateAction({
      payload: {
        deviceName,
        manufacturer: manufacturer === '' ? undefined : manufacturer,
        serialNumber: serialNumber === '' ? undefined : serialNumber,
        manufactured: String(manufactured) === '' ? undefined : manufactured,
        internalPossessionsNumber,
        internalNote,
        building,
        floor,
        room,
        longitude,
        latitude,
        deviceTypeId: Number(deviceTypeId),
        deviceSubtypeId: Number(deviceSubtypeId),
        departmentId: Number(departmentId),
        responsiblePersonId: responsiblePersonId ? Number(responsiblePersonId) : undefined,
        responsiblePersonNote,
        nextRevision: mappedRevisions,
        files: mappedFiles,
        evidenceFields: Object.values(evidenceFields),
        structure,
        parentDeviceId: parentDeviceId ? Number(parentDeviceId) : undefined,
        childDeviceIds: childDeviceIds ? childDeviceIds.map(Number) : undefined,
      },
    })
      .then((response) => {
        notifications.show({
          title: 'Zariadenie bolo skopírované',
          message: `Zariadenie ${
            originalDevice!.deviceName
          } bolo úspešne skopírované a zariadenie ${deviceName} bol úspešne vytvorený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (DEVICE_REDIRECT_AFTER_SAVE === 'same') {
          navigate(EDIT_DEVICE_PAGE_PATH.insert({ deviceId: response.deviceId }), { replace: true });
        } else if (DEVICE_REDIRECT_AFTER_SAVE === 'list') {
          navigate(DEVICES_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  return (
    <DashboardLayout
      title={`Skopírovanie zariadenia: ${originalDevice?.deviceName ?? 'Načítavam...'}`}
      breadcrumbs={[{ title: 'Zariadenia', link: DEVICES_PAGE_PATH.original }, { title: 'Skopírovanie zariadenia' }]}
    >
      {!loading && originalDevice && (
        <DeviceForm
          onSubmit={onSubmit}
          initialDepartment={originalDevice.department}
          initialValues={{
            deviceName: `${originalDevice.deviceName} (Kópia)`,
            manufacturer: originalDevice.manufacturer ?? '',
            serialNumber: originalDevice.serialNumber ?? '',
            manufactured: originalDevice.manufactured ?? undefined,
            internalPossessionsNumber: originalDevice.internalPossessionsNumber ?? '',
            internalNote: originalDevice.internalNote ?? '',
            building: originalDevice.building ?? '',
            floor: originalDevice.floor ?? '',
            room: originalDevice.room ?? '',
            longitude: originalDevice.longitude ? parseFloat(originalDevice.longitude) : undefined,
            latitude: originalDevice.latitude ? parseFloat(originalDevice.latitude) : undefined,
            deviceTypeId: String(originalDevice.deviceType.deviceTypeId),
            deviceSubtypeId: String(originalDevice.deviceSubtype.deviceTypeId ?? 0),
            departmentId: String(originalDevice.department.departmentId),
            responsiblePersonId: originalDevice.responsiblePerson
              ? String(originalDevice.responsiblePerson.userId)
              : '',
            responsiblePersonNote: originalDevice.responsiblePersonNote ?? '',
            externalId: '',
            files: retrieveDeviceFiles(originalDevice.files, 'department'),
            nextRevision: originalDevice.nextRevision
              ? originalDevice.nextRevision.map(
                  ({ description, date, revisionPeriod, externalId, neverExecuted, deviceTypeId }) => {
                    let parsedDate = '';
                    let parsedNextDate = '';

                    if (date) {
                      try {
                        parsedDate = stringifyDate(addMonths(new Date(date), -revisionPeriod));
                        parsedNextDate = stringifyDate(new Date(date));
                      } catch (error: any) {
                        panic(new Error(`Invalid date: ${date}`));
                      }
                    }

                    return {
                      description,
                      date: neverExecuted ? '' : parsedDate,
                      nextDate: parsedNextDate,
                      revisionPeriod: String(revisionPeriod),
                      key: randomId(),
                      externalId,
                      deleted: false,
                      neverExecuted,
                      deviceTypeId: deviceTypeId ? String(deviceTypeId) : '',
                    };
                  }
                )
              : [
                  {
                    description: '',
                    date: '',
                    revisionPeriod: '',
                    key: randomId(),
                    deleted: false,
                    neverExecuted: false,
                  },
                ],
            // Explicitly exclude faults - don't copy them
            faults: [],
            evidenceFields: originalDevice.evidenceFields.reduce((acc, field) => {
              // the API returns string for decimal numbers
              if (field.fieldType === 'number') {
                field.fieldNumberValue =
                  field.fieldNumberValue === undefined ? undefined : Number(field.fieldNumberValue);
              }

              acc[field.evidenceFieldId!] = field;

              return acc;
            }, {} as { [evidenceFieldId: number]: any }),
            structure: originalDevice.structure ?? 'simple',
            parentDeviceId: originalDevice.parentDevice ? String(originalDevice.parentDevice.deviceId) : '',
            childDeviceIds: originalDevice.childDevices?.map((child) => String(child.deviceId)),
          }}
          readOnly={{
            faults: true, // Faults are excluded, so make it read-only
          }}
          isAddMode
          showQrId={false}
          primaryButtonText="Skopírovať"
          primaryIcon={<IconCopy stroke={1.5} height={24} width={24} />}
        />
      )}
    </DashboardLayout>
  );
}
